import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  type Ref,
} from 'react';
import { ArrowLeft, ArrowRight, RotateCw, Upload, X } from 'lucide-react';
import {
  STICKER_UPLOAD_MAX_BYTES,
  clampFrontStickerPlacement,
  type CardDetail,
  type PlacedSticker,
  type StickerSurface,
} from '@molt/shared';
import { Button, Dialog, IconButton, Notice, Popover, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { SETTLED, cardHeight } from '@/lib/density';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  STICKER_UPLOAD_ACCEPT,
  isStickerUploadContentType,
  stickerImageSrc,
  stickerLabel,
  useBoardStickers,
  useMoveSticker,
  usePlaceSticker,
  useRemoveSticker,
  useUploadCustomSticker,
  type StickerTranslate,
} from '@/lib/stickers';

export interface CardStickerSectionProps {
  card: CardDetail;
  boardId: string;
  canEdit: boolean;
  /**
   * Render ONLY the trigger control, for the card back's "Add to card" row
   * (`docs/design/stage2/card-back.md` D1): no heading, no empty-state prose.
   * The trigger keeps its accessible name and its popover is unchanged. A
   * viewer who cannot edit gets nothing, because an empty section with no
   * control in it says nothing the card back needs to say.
   */
  compact?: boolean | undefined;
  /**
   * The trigger, so the card back can focus it when this section first moves
   * into the body because the member just filled it from its compact trigger
   * in the "Add to card" row, which unmounted with focus inside it.
   */
  triggerRef?: Ref<HTMLButtonElement> | undefined;
}

function clampPosition(value: number): number {
  return Math.max(0, Math.min(192, value));
}

/**
 * Where a newly placed sticker lands, per surface -- and the two are NOT the
 * same point, because the two surfaces are not the same shape.
 *
 * `back` keeps `72, 40`, the value B58 shipped: the card back's canvas is the
 * `h-36` (144px) box below, and 72/40 is roughly its middle-left.
 *
 * `front` is `180, 8`, and it is DERIVED rather than picked. `lib/density.ts`
 * holds `columnWidth: 292`, and `docs/notes/density-budget.md` measures the
 * reference card at 107.1px tall -- so a card front is about 272 x 107 with a
 * left-aligned title at the top and `CardBadges`' strip along the bottom.
 * `FR-CARD-032`'s own edge case is *"stickers placed on a full-cover card must
 * not obscure the title to the point of illegibility"*, so the default drops
 * into the TOP-RIGHT corner, which is the only region of a card front that is
 * neither title nor badge strip. 180 also stays inside `clampPosition`'s 192
 * ceiling, so the two nudge controls can move a front sticker without the very
 * first press snapping it backwards.
 *
 * ⚠ These are DEFAULTS, not a contract. The card front's exact geometry is
 * `CardTile`'s, and this file must not encode an assumption `CardTile` has to
 * honour -- `positionX`/`positionY` are `numeric(8,4)` and free placement is
 * `FR-CARD-032`'s behaviour text.
 */
const SURFACE_DROP_POINT: Record<StickerSurface, { x: number; y: number }> = {
  front: { x: 180, y: 8 },
  back: { x: 72, y: 40 },
};

/**
 * The card front's NOMINAL box, and the only reason this file has one.
 *
 * `clampFrontStickerPlacement` takes the tile's measured
 * `getBoundingClientRect()`. The board-menu tray has one, because it drops onto
 * a card that is on screen. **This section does not**: the card back never
 * renders a card front, so a front placement made here is aimed at a box that
 * is not in this document. The clamp still has to run -- the alternative is a
 * front default that is contained only by coincidence -- so it runs against the
 * reference card `docs/notes/density-budget.md` measures, and the two terms are
 * READ OFF `lib/density.ts` rather than written down again:
 *
 *   width  = `columnWidth` 292 - `panelPadding` 8 either side   = **276**
 *   height = `cardHeight('labelled')`                           = **107.2**
 *
 * `labelled` is the shape, not `plain` or `minimal`, because density-budget
 * calls it *"the honest default for a board that uses labels at all"* and it is
 * the same 107.2px card `SURFACE_DROP_POINT.front`'s own derivation above
 * already cites. Deriving both terms means a restyle of the column or the card
 * front moves this bound with it; a literal `276` here would drift silently,
 * and the symptom of the drift is a sticker clipped away by `CardTile`'s
 * `overflow-hidden` -- placed by a member who then cannot see it, with nothing
 * red anywhere.
 *
 * ⚠ NOMINAL, and the word is load-bearing. A real card front is not always
 * 107.2px tall: a full-cover card is `min-h-[5.75rem]` plus its content, and a
 * two-line title is taller than a one-line one. So this box bounds a
 * coordinate THIS FILE INVENTS, and nothing else. It is deliberately NOT
 * applied to `nudge` -- see there.
 */
const CARD_FRONT_NOMINAL_BOX = {
  width: SETTLED.columnWidth - SETTLED.panelPadding * 2,
  height: cardHeight('labelled'),
} as const;

/**
 * ⚠ MODULE SCOPE, outside the component, so it cannot read `t` from context:
 * the translator is a required leading parameter here for the same reason the
 * `lib/stickers.ts` exports take one. Both keys are SHARED with
 * `useUploadCustomSticker`'s `mutationFn`, which guards on the same constant and
 * the same predicate -- one string, one `<Notice tone="bad">`, two authors.
 */
function stickerUploadError(t: StickerTranslate, file: File): string | null {
  if (file.size > STICKER_UPLOAD_MAX_BYTES) {
    return t('cardBack.stickers.upload.tooLarge');
  }
  if (!isStickerUploadContentType(file.type)) {
    return t('cardBack.stickers.upload.unsupportedType');
  }
  return null;
}

export function CardStickerSection({
  card,
  boardId,
  canEdit,
  compact = false,
  triggerRef = null,
}: CardStickerSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * FE-58.1 -- which surface the NEXT placement lands on.
   *
   * `back` is the default, and deliberately: it is what every placement made
   * through this section did before the board face could render a front
   * sticker at all, so an existing member's next click does exactly what their
   * last one did. The front is now a choice rather than a refusal.
   */
  const [surface, setSurface] = useState<StickerSurface>('back');
  const [removeTarget, setRemoveTarget] = useState<PlacedSticker | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  // ⚠ `useBoardStickers` announces nothing and keeps its two-argument shape;
  // the other four take the translator first.
  const stickers = useBoardStickers(boardId, pickerOpen);
  const placeSticker = usePlaceSticker(t, boardId, card.id);
  const moveSticker = useMoveSticker(t, boardId, card.id);
  const removeSticker = useRemoveSticker(t, boardId, card.id);
  const uploadSticker = useUploadCustomSticker(t, boardId);
  // The existing shared key, not a new one -- `CardDatesSection` and
  // `CardLocationSection` both reuse it for this expression. ⚠ The UNTRIMMED
  // title is passed when the trim is non-empty, matching `CardDatesSection`;
  // aligning it with `CardLocationSection`'s trimmed form would change a
  // rendered accessible name that `FR-CARD-032` reads.
  const title = card.title.trim() === '' ? t('cardBack.untitledCard') : card.title;
  /**
   * ⚠ THIS FILTER USED TO BE A CEILING AND IS NOW A SPLIT, and that is the
   * whole of what `FE-58.1` changes here.
   *
   * Through B58 this section read `surface === 'back'` and dropped every front
   * placement on the floor: a front sticker was invisible here, and -- because
   * `GET /boards/:id/contents` projected no stickers at all -- invisible on the
   * board face too, which is what `cardBack.stickers.frontUnsupported` used to
   * announce in the picker. The board-face projection now carries
   * `surface = 'front'` rows (`boardFaceCardSchema.stickers`), so the front is
   * a real surface, and the card back has to be able to show, move, rotate and
   * REMOVE what lands there: `FR-CARD-032` AC2's *"hovering it and clicking
   * remove"* has no other home, because the card front renders stickers as
   * decoration with no controls on it.
   *
   * `ck_card_stickers_surface CHECK (surface IN ('front','back'))` is what
   * makes this a closed two-valued split rather than an open string test --
   * there is no third arm to lose a row to.
   */
  const frontPlaced = card.stickers.filter((sticker) => sticker.surface === 'front');
  const backPlaced = card.stickers.filter((sticker) => sticker.surface === 'back');
  const targetPlaced = surface === 'front' ? frontPlaced : backPlaced;
  const uploadDisabled =
    uploadFile === null || uploadError !== null || uploadSticker.isPending;

  /**
   * Where the placement this section makes actually lands on the wire.
   *
   * ⚠ THE CLAMP IS AN INPUT-SIDE ACT AND RUNS EXACTLY ONCE, HERE, BEFORE THE
   * POST. `CardTile`'s docstring forbids the other reading in as many words --
   * *"`left`/`top` are the wire's own pixels from THIS box's origin. No scale
   * factor and no front-specific clamp"* -- and that stands: every reader
   * afterwards, this section's own canvas included, still renders
   * `sticker.positionX` verbatim, so `FR-CARD-032` AC4's *"another member
   * opening the board sees it in the same position"* is untouched. There is
   * still exactly one number; this is only where it is made legal.
   *
   * ⚠ FRONT ONLY, and `back` is not an oversight. The back surface is edited on
   * the `h-36` canvas below, which is a real box in THIS document with its own
   * `clampPosition` ceiling; `clampFrontStickerPlacement` bounds a coordinate
   * to a CARD FRONT (`STICKER_FRONT_RENDER_PX`, `CardTile`'s `size-8`) and
   * means nothing on the back. `ck_card_stickers_surface CHECK (surface IN
   * ('front','back'))` is what makes this a closed two-armed choice rather than
   * an open string test.
   *
   * It is a no-op on today's `180, 8` -- deliberately, and that is the point.
   * The default was contained by ARITHMETIC IN A COMMENT before; it is now
   * contained by the same function the tray's pointer drop and its keyboard
   * placement call, so the three cannot disagree, and a future edit that moves
   * the default outside the box is corrected rather than shipped.
   */
  function place(stickerId: string, stickerName: string) {
    const drop = SURFACE_DROP_POINT[surface];
    const placement =
      surface === 'front'
        ? clampFrontStickerPlacement({
            positionX: drop.x,
            positionY: drop.y,
            tileWidth: CARD_FRONT_NOMINAL_BOX.width,
            tileHeight: CARD_FRONT_NOMINAL_BOX.height,
          })
        : { positionX: drop.x, positionY: drop.y };
    placeSticker.mutate(
      {
        stickerId,
        stickerName,
        surface,
        positionX: placement.positionX,
        positionY: placement.positionY,
        rotation: 0,
        // ⚠ The stack is PER SURFACE, not per card. `zIndex` orders the
        // placements drawn on ONE surface against each other; seeding a front
        // placement from the card's whole count would leave gaps that mean
        // nothing, and `FR-CARD-032`'s *"stacking order adjustable after
        // placement"* is a property of the surface the sticker sits on.
        zIndex: targetPlaced.length,
      },
      {
        onSuccess: () => {
          announce(t('cardBack.stickers.announce.added', { sticker: stickerName }));
          setPickerOpen(false);
        },
      },
    );
  }

  /**
   * ⚠ THIS DELIBERATELY DOES NOT USE `clampFrontStickerPlacement`, AND THE
   * OMISSION IS THE DECISION.
   *
   * `place` above invents a coordinate and so must bound it (`place`'s
   * docstring). A nudge moves a coordinate SOMEBODY ALREADY CHOSE -- either
   * here, or by dropping onto a card front whose box was actually MEASURED. The
   * nominal box above is the 107.2px reference card, and a real card front is
   * frequently taller: a full-cover card is `min-h-[5.75rem]` plus its content,
   * a two-line title is taller than a one-line one. So re-clamping a front
   * placement to the nominal box here would silently drag a sticker UPWARDS
   * that is perfectly legal where it sits, on a card this section cannot see --
   * which is precisely the *"silently relocating a coordinate a member
   * deliberately chose"* failure the chunk's audit declines by name.
   *
   * ---------------------------------------------------------------------------
   * ⚠ THE X AXIS IS THE EXCEPTION, AND IT IS A REPAIR, NOT A TIDY-UP
   * ---------------------------------------------------------------------------
   * The two axes are NOT symmetric, because the card front's two extents are
   * not equally settled:
   *
   *   · **Width is fixed.** A card front is always `columnWidth` less
   *     `ListPanel`'s padding — `--spacing-list` is one declaration and every
   *     column in the product is that wide. So `CARD_FRONT_NOMINAL_BOX.width`
   *     is not nominal at all; it is the width.
   *   · **Height varies**, per the box's own docstring, so 107.2 really is only
   *     a reference.
   *
   * And `clampPosition`'s 192 is **NARROWER** than the front's real x bound of
   * 244 (`276 - 32`). Left alone, a front sticker that the board-menu tray
   * places at `x = 230` against a MEASURED tile snaps back to 192 on the
   * member's very first nudge — the same *"silently relocating a coordinate
   * somebody deliberately chose"* failure this docstring declines on the y
   * axis, arriving from the opposite direction. It becomes live the moment the
   * tray this chunk adds can drop past 192, so it is repaired here rather than
   * left for the tray to trip over.
   *
   * ⚠ The arm is chosen from `sticker.surface` — THIS placement's surface — and
   * never from the component's `surface` state, which is the target of the
   * NEXT placement and says nothing about the sticker being moved.
   *
   * The y axis keeps `clampPosition` on both surfaces: `dy` is `0` at both call
   * sites (the two controls are `moveLeft`/`moveRight`), and 192 is past any
   * real card-front height, so it constrains nothing a member can reach.
   * `clampPosition` is otherwise left exactly as `B58` shipped it — it is the
   * ceiling of the editor canvas below, and not a claim about card-front
   * geometry.
   */
  function nudge(sticker: PlacedSticker, dx: number, dy: number) {
    moveSticker.mutate({
      placementId: sticker.id,
      stickerName: stickerLabel(t, sticker.sticker),
      body: {
        positionX:
          sticker.surface === 'front'
            ? clampFrontStickerPlacement({
                positionX: sticker.positionX + dx,
                positionY: 0,
                tileWidth: CARD_FRONT_NOMINAL_BOX.width,
                tileHeight: CARD_FRONT_NOMINAL_BOX.height,
              }).positionX
            : clampPosition(sticker.positionX + dx),
        positionY: clampPosition(sticker.positionY + dy),
      },
    });
  }

  function rotate(sticker: PlacedSticker) {
    moveSticker.mutate({
      placementId: sticker.id,
      stickerName: stickerLabel(t, sticker.sticker),
      body: { rotation: Number(((sticker.rotation + 15) % 360).toFixed(2)) },
    });
  }

  function confirmRemove() {
    if (removeTarget === null) return;
    const label = stickerLabel(t, removeTarget.sticker);
    removeSticker.mutate(
      { placementId: removeTarget.id, stickerName: label },
      {
        onSuccess: () => {
          announce(t('cardBack.stickers.announce.removed', { sticker: label }));
          setRemoveTarget(null);
        },
      },
    );
  }

  function selectUploadFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0] ?? null;
    setUploadFile(file);
    setUploadMessage(null);
    if (file === null) {
      setUploadError(null);
      return;
    }
    setUploadError(stickerUploadError(t, file));
  }

  async function submitUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (uploadFile === null) return;
    const error = stickerUploadError(t, uploadFile);
    if (error !== null) {
      setUploadError(error);
      return;
    }

    setUploadError(null);
    try {
      const sticker = await uploadSticker.mutateAsync({
        file: uploadFile,
        name: uploadName,
      });
      const label = stickerLabel(t, sticker);
      // ONE key, two sinks: the live region and the visible `role="status"`
      // paragraph below. Two keys would let a translator drift two strings this
      // branch guarantees identical.
      const uploaded = t('cardBack.stickers.upload.complete', { sticker: label });
      announce(uploaded);
      setUploadMessage(uploaded);
      setUploadFile(null);
      setUploadName('');
      if (fileInputRef.current !== null) fileInputRef.current.value = '';
    } catch (cause) {
      setUploadError(
        cause instanceof Error
          ? cause.message
          : t('cardBack.stickers.error.uploadFailed'),
      );
    }
  }

  /**
   * One surface's placements, on its own captioned canvas.
   *
   * ⚠ BOTH surfaces render, and the caption is what makes that legible. A card
   * can now carry front stickers and back stickers at once, and an uncaptioned
   * second box would be two identical grey rectangles whose difference is
   * invisible. The caption reuses the two toggle labels above rather than
   * declaring a second pair: the referent is identical by construction, and
   * `cardBack.stickers.upload.complete`'s recorded rule -- *"ONE key, two
   * sinks: two keys would let a translator drift two strings this branch
   * guarantees identical"* -- is exactly this shape.
   *
   * ⚠ The canvas is the SAME `h-36` box on both surfaces, and it is an EDITOR,
   * not a preview of the card front. `clampPosition`'s 192 ceiling, the 12px
   * nudge and the 15-degree rotate step are all unchanged; the card front's own
   * rendering of these placements is `CardTile`'s, off
   * `boardFaceCardSchema.stickers`.
   */
  function stickerCanvas(
    canvasSurface: StickerSurface,
    placements: readonly PlacedSticker[],
  ): ReactElement {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium text-ink-muted">
          {canvasSurface === 'front'
            ? t('cardBack.stickers.surface.front')
            : t('cardBack.stickers.surface.back')}
        </p>
        <div className="relative h-36 overflow-hidden rounded-card border border-border bg-surface-raised">
          {placements.map((sticker) => {
            const label = stickerLabel(t, sticker.sticker);
            return (
              <div
                key={sticker.id}
                className="absolute flex flex-col items-center gap-1"
                style={{
                  left: sticker.positionX,
                  top: sticker.positionY,
                  zIndex: sticker.zIndex,
                  rotate: `${sticker.rotation}deg`,
                }}
              >
                <img
                  src={stickerImageSrc(sticker.sticker.renderUrl)}
                  alt={sticker.sticker.name ?? ''}
                  aria-hidden={sticker.sticker.name === null ? true : undefined}
                  className="size-12 object-contain"
                />
                {canEdit ? (
                  <div className="flex rounded-card border border-border-strong bg-surface-raised shadow-card">
                    <IconButton
                      label={t('cardBack.stickers.moveLeft', { sticker: label })}
                      size="sm"
                      variant="quiet"
                      onClick={() => nudge(sticker, -12, 0)}
                    >
                      <ArrowLeft aria-hidden="true" className="size-3.5" />
                    </IconButton>
                    <IconButton
                      label={t('cardBack.stickers.moveRight', { sticker: label })}
                      size="sm"
                      variant="quiet"
                      onClick={() => nudge(sticker, 12, 0)}
                    >
                      <ArrowRight aria-hidden="true" className="size-3.5" />
                    </IconButton>
                    <IconButton
                      label={t('cardBack.stickers.rotate', { sticker: label })}
                      size="sm"
                      variant="quiet"
                      onClick={() => rotate(sticker)}
                    >
                      <RotateCw aria-hidden="true" className="size-3.5" />
                    </IconButton>
                    <IconButton
                      label={t('cardBack.stickers.removeLabel', {
                        sticker: label,
                        title,
                      })}
                      size="sm"
                      variant="quiet"
                      onClick={() => setRemoveTarget(sticker)}
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </IconButton>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const editor = canEdit ? (
    <Popover
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      title={t('cardBack.stickers.pickerTitle')}
      width={320}
      trigger={
        <Button
          ref={triggerRef}
          type="button"
          size="sm"
          variant="secondary"
          aria-label={t('cardBack.stickers.addLabel', { title })}
        >
          {t('cardBack.stickers.add')}
        </Button>
      }
      closeControl={
        <IconButton label={t('cardBack.stickers.closePicker')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
    >
      <div className="flex flex-col gap-3">
        {stickers.error ? <Notice tone="bad" body={stickers.error.message} /> : null}
        {/*
         * FE-58.1 -- THE SURFACE CHOICE STANDS EXACTLY WHERE THE REFUSAL
         * STOOD. Until this chunk the picker carried
         * `<Notice tone="info" body={t('cardBack.stickers.frontUnsupported')} />`
         * -- *"Card-front stickers need board payload support before they
         * can render on the board."* -- and that sentence was TRUE:
         * `GET /boards/:id/contents` projected no placed stickers, so a
         * front placement rendered nowhere. `boardFaceCardSchema.stickers`
         * closes exactly that gap, which makes the sentence FALSE, so it
         * is not softened or moved -- the control it was standing in for
         * replaces it.
         *
         * ⚠ `variant='toggle'` rather than two `secondary` buttons and a
         * hand-written `aria-pressed`: `Button`'s docstring reserves
         * `toggle` for *"one control with two states, carrying
         * `aria-pressed`"*, and `segment` for two links where one is
         * current. This is state, not a URL, so it is `toggle` -- twice,
         * inside one `role='group'`, so a screen reader hears which
         * surface the next placement lands on before it hears the
         * library.
         *
         * ⚠ Two literal `t()` calls rather than one over a computed key.
         * `CardStickerSection.test.tsx`'s *"declares every key it
         * routes"* sweeps the RENDERED text for a `cardBack.` prefix,
         * which catches an undeclared key -- but a key assembled from an
         * expression is invisible to every reader who greps this file for
         * the string, and the catalogue is greppable by design.
         */}
        <div
          role="group"
          aria-label={t('cardBack.stickers.surface.label')}
          className="flex flex-wrap gap-1"
        >
          <Button
            type="button"
            size="sm"
            variant="toggle"
            pressed={surface === 'front'}
            onClick={() => setSurface('front')}
          >
            {t('cardBack.stickers.surface.front')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="toggle"
            pressed={surface === 'back'}
            onClick={() => setSurface('back')}
          >
            {t('cardBack.stickers.surface.back')}
          </Button>
        </div>
        {stickers.isPending ? (
          <p className="text-sm text-ink-muted">
            {t('cardBack.stickers.loadingLibrary')}
          </p>
        ) : stickers.data?.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('cardBack.stickers.libraryEmpty')}</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {(stickers.data ?? []).map((sticker) => {
              const label = stickerLabel(t, sticker);
              return (
                <Button
                  key={sticker.id}
                  variant="quiet"
                  className="h-20 border border-border-strong p-2"
                  // The column lives on `Button`'s content span: `flex-col`
                  // on the button itself never reached the image and label.
                  contentClassName="flex min-w-0 max-w-full flex-col items-center gap-1"
                  aria-label={t('cardBack.stickers.placeLabel', {
                    sticker: label,
                    title,
                  })}
                  disabled={placeSticker.isPending}
                  onClick={() => place(sticker.id, label)}
                >
                  <img
                    src={stickerImageSrc(sticker.renderUrl)}
                    alt=""
                    aria-hidden="true"
                    className="size-10 object-contain"
                  />
                  <span className="block max-w-full truncate text-xs">{label}</span>
                </Button>
              );
            })}
          </div>
        )}
        <form
          aria-label={t('cardBack.stickers.upload.formLabel', { title })}
          className="flex flex-col gap-2 border-t border-border pt-3"
          onSubmit={(event) => {
            void submitUpload(event);
          }}
        >
          <label htmlFor={fileInputId} className="text-sm font-medium text-ink">
            {t('cardBack.stickers.upload.fileLabel')}
          </label>
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept={STICKER_UPLOAD_ACCEPT}
            className="block w-full rounded-card border border-border-strong bg-surface px-2 py-1.5 text-sm text-ink file:mr-2 file:rounded-card file:border-0 file:bg-accent file:px-2 file:py-1 file:text-sm file:font-medium file:text-accent-ink"
            onChange={selectUploadFile}
          />
          <TextField
            label={t('cardBack.stickers.upload.nameLabel')}
            value={uploadName}
            onChange={(event) => setUploadName(event.currentTarget.value)}
            maxLength={255}
          />
          {uploadError !== null ? <Notice tone="bad" body={uploadError} /> : null}
          {uploadMessage !== null ? (
            <p role="status" className="text-sm text-ink-muted">
              {uploadMessage}
            </p>
          ) : null}
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            iconStart={<Upload className="size-4" />}
            busy={uploadSticker.isPending}
            busyLabel={t('cardBack.stickers.upload.busy')}
            disabled={uploadDisabled}
          >
            {t('cardBack.stickers.upload.submit')}
          </Button>
        </form>
      </div>
    </Popover>
  ) : null;

  if (compact) return editor;

  return (
    <section aria-labelledby="card-stickers-heading" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="card-stickers-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.stickers.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {editor}
      </div>

      {card.stickers.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('cardBack.stickers.empty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {frontPlaced.length > 0 ? stickerCanvas('front', frontPlaced) : null}
          {backPlaced.length > 0 ? stickerCanvas('back', backPlaced) : null}
        </div>
      )}

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        /*
         * ⚠ THE TERNARY IS LOAD-BEARING, not tidy. JSX props evaluate eagerly,
         * so this `t()` fires on every render of the section whether the dialog
         * is open or closed, and `interfaceMessage` THROWS on a slot whose value
         * is `undefined`. The empty STRING is a value and does not throw, and
         * `removeTarget?.sticker` would turn every render into an exception.
         * The closed-state render is `Remove sticker ?`, byte-identical to what
         * the template produced, and discarded unrendered: `Dialog` portals with
         * no `forceMount`.
         */
        title={t('cardBack.stickers.remove.title', {
          sticker: removeTarget === null ? '' : stickerLabel(t, removeTarget.sticker),
        })}
        description={t('cardBack.stickers.remove.description')}
        footer={
          <>
            <Button variant="quiet" onClick={() => setRemoveTarget(null)}>
              {t('cardBack.stickers.remove.cancel')}
            </Button>
            <Button
              variant="danger"
              busy={removeSticker.isPending}
              onClick={confirmRemove}
            >
              {t('cardBack.stickers.remove.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">{t('cardBack.stickers.remove.body')}</p>
      </Dialog>
    </section>
  );
}
